/** Read-freshness tracker (2026-07-17 incident pin).
 *
 * During the v1 outage every device silently rendered its last-synced copy —
 * Tom watched a frozen board for hours with no hint it was stale (and later
 * mistook the catch-up for data moving on its own). v2's rule: when reads
 * are failing, SAY SO, on screen, with the age of what's being shown.
 *
 * Fed by the http layer (every GET reports here); consumed by the StaleBar
 * in app.tsx. Deliberately tiny: two counters + a subscription.
 */

let lastReadOk = 0; // epoch ms of the last successful GET anywhere
let consecFails = 0; // consecutive failed GETs (any origin)
const subs = new Set<() => void>();

const ping = (): void => {
  for (const fn of subs) fn();
};

export function noteReadOk(): void {
  lastReadOk = Date.now();
  consecFails = 0;
  ping();
}

export function noteReadFail(): void {
  consecFails++;
  ping();
}

export interface Freshness {
  /** true = reads are failing AND what's on screen is old enough to matter */
  stale: boolean;
  /** ms since the last successful read; null = never succeeded this session */
  sinceMs: number | null;
  fails: number;
}

/** Stale = 2+ consecutive read failures AND nothing fresh in the last 45s.
 * One blip never flags; a dead network flags within two poll cycles. */
export function freshness(now: number = Date.now()): Freshness {
  const sinceMs = lastReadOk ? now - lastReadOk : null;
  const stale = consecFails >= 2 && (sinceMs === null || sinceMs > 45_000);
  return { stale, sinceMs, fails: consecFails };
}

export function subscribeHealth(fn: () => void): () => void {
  subs.add(fn);
  return () => {
    subs.delete(fn);
  };
}

/** test hook */
export function _resetHealth(): void {
  lastReadOk = 0;
  consecFails = 0;
}

/** "just now" / "4m ago" / "2h ago" — for the stale banner. */
export function fmtStaleAge(sinceMs: number | null): string {
  if (sinceMs === null) return "";
  const m = Math.floor(sinceMs / 60_000);
  if (m < 1) return "moments ago";
  if (m < 60) return m + "m ago";
  const h = Math.floor(m / 60);
  return h + "h " + (m % 60) + "m ago";
}
